import { Injectable, inject, signal } from '@angular/core';
import {
  BaseUser,
  Permissions,
  UserType,
  userTypeFromJSON,
} from '@enrollhere/classes';
import { Observable, ReplaySubject } from 'rxjs';
import type { ProcessAwarePermissions } from '../data/process-permission-shapes';
import { EnrollhereApiService } from '../enrollhere-api/enrollhere-api';
import { CommonAuthService } from './common-auth.service';
import { clearGates } from './persisted-gate-cache';

export interface ConsolidatedPermissionsSnapshot {
  p: ProcessAwarePermissions;
  sad: boolean;
  userType: UserType;
  hasTags: boolean;
  allTags: string[];
  /** Policy search is limited to team tags. Hides duplicate review. */
  policyTagsLimited?: boolean;
  agencyId?: string;
  sourceAgencyIds?: string[];
  /** Switcher selection this snapshot was fetched for; empty = all agencies. */
  selectionAgencyIds?: string[];
}

export const NEUTRAL_PERMISSIONS_SNAPSHOT: ConsolidatedPermissionsSnapshot = {
  p: {} as ProcessAwarePermissions,
  sad: false,
  userType: UserType.UNKNOWN_TYPE,
  hasTags: false,
  allTags: [],
  sourceAgencyIds: [],
  agencyId: undefined,
  selectionAgencyIds: [],
};

interface ConsolidatedPermissionsResponse {
  p: Record<string, unknown>;
  sad: boolean;
  userType: string;
  hasTags: boolean;
  allTags: string[];
  policyTagsLimited?: boolean;
  agencyId?: string;
  sourceAgencyIds?: string[];
}

/**
 * Consolidated permissions drive the sidebar menu and the home shortcuts, so
 * until the fetch lands both render empty and then pop in. The last snapshot is
 * kept per user and selection so a reload paints from it immediately; the
 * network answer always overwrites it moments later, and the server remains the
 * only thing that actually enforces access.
 */
const SNAPSHOT_CACHE_PREFIX = 'eh.consolidated.v1.';

/**
 * `getForAgency` only deduped calls that overlapped in flight, so every menu
 * rebuild re-fetched each agency in scope: ~16 agencies x ~11 rebuilds is
 * hundreds of requests for a value that cannot change that fast. Hold the
 * result briefly — long enough to collapse a burst, short enough that a
 * permission change still lands quickly.
 */
const AGENCY_SNAPSHOT_TTL_MS = 30_000;
const SNAPSHOT_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

interface CachedSnapshot {
  savedAt: number;
  snapshot: ConsolidatedPermissionsSnapshot;
}

@Injectable({ providedIn: 'root' })
export class CommonPermissionService {
  private readonly api = inject(EnrollhereApiService);
  private readonly auth = inject(CommonAuthService);
  private readonly consolidatedSubject =
    new ReplaySubject<ConsolidatedPermissionsSnapshot>(1);
  private readonly inflight = new Map<
    string,
    Promise<ConsolidatedPermissionsSnapshot>
  >();
  private readonly agencyInflight = new Map<
    string,
    Promise<ConsolidatedPermissionsSnapshot>
  >();
  private readonly agencySnapshots = new Map<
    string,
    { snapshot: ConsolidatedPermissionsSnapshot; expiresAt: number }
  >();
  private latestRequestKey: string | null = null;
  private requestEpoch = 0;

  private readonly _consolidated =
    signal<ConsolidatedPermissionsSnapshot | null>(null);
  private readonly _selectedAgencyIds = signal<string[]>([]);
  private readonly _loading = signal(false);
  private readonly _loadError = signal<unknown>(null);

  readonly consolidated = this._consolidated.asReadonly();
  /**
   * Agency ids explicitly chosen in the app switcher, empty for "My Profile"
   * (all agencies). Set synchronously when the selection changes so callers
   * scope to the selection without waiting on — or depending on the success of
   * — the consolidated fetch.
   */
  readonly selectedAgencyIds = this._selectedAgencyIds.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly loadError = this._loadError.asReadonly();
  readonly consolidated$: Observable<ConsolidatedPermissionsSnapshot> =
    this.consolidatedSubject.asObservable();

  /**
   * Publishes the app switcher selection. **Only** the auth selection stream may
   * call this: every other consumer must use `ensureConsolidated()`.
   *
   * Consumers used to pass their own selection here, but they capture it before
   * awaiting, so a call that started before a switch lands after it and resets
   * the scope to "all agencies" — the selected agency's list silently widens to
   * every agency.
   */
  setSelection(
    selection: BaseUser[] | null | undefined
  ): Promise<ConsolidatedPermissionsSnapshot> {
    const agencyIds = this.normalizeSelectedAgencyIds(selection);
    this._selectedAgencyIds.set(agencyIds);
    return this.load(agencyIds);
  }

  /**
   * @deprecated Prefer `setSelection` (auth stream) or `ensureConsolidated`
   * (read-only refresh). Kept so consumer main still builds against this
   * submodule before host call sites are updated.
   */
  refreshForSelection(
    selection: BaseUser[] | null | undefined
  ): Promise<ConsolidatedPermissionsSnapshot> {
    return this.setSelection(selection);
  }

  /** Consolidated permissions for the active selection; never changes it. */
  ensureConsolidated(): Promise<ConsolidatedPermissionsSnapshot> {
    return this.load(this._selectedAgencyIds());
  }

  private async load(
    agencyIds: string[]
  ): Promise<ConsolidatedPermissionsSnapshot> {
    const epoch = this.requestEpoch;
    const key = agencyIds.join('|');
    this.latestRequestKey = key;
    const existing = this.inflight.get(key);
    if (existing) {
      return existing;
    }

    // Only before anything real has landed: a cached snapshot must never
    // overwrite a fresher one already in hand.
    if (this._consolidated() === null) {
      const cached = this.readCachedSnapshot(agencyIds);
      if (cached) {
        this._consolidated.set(cached);
        this.consolidatedSubject.next(cached);
      }
    }

    const promise = this.fetchConsolidated(agencyIds)
      .then(snapshot => {
        if (this.isCurrentRequest(epoch, key)) {
          this._consolidated.set(snapshot);
          this.consolidatedSubject.next(snapshot);
          this._loadError.set(null);
          this.writeCachedSnapshot(agencyIds, snapshot);
          return snapshot;
        }
        return { ...NEUTRAL_PERMISSIONS_SNAPSHOT };
      })
      .catch((err: unknown) => {
        if (this.isCurrentRequest(epoch, key)) {
          this._loadError.set(err);
        }
        throw err;
      })
      .finally(() => {
        if (this.inflight.get(key) === promise) {
          this.inflight.delete(key);
        }
        if (epoch === this.requestEpoch) {
          this._loading.set(this.inflight.size > 0);
        }
      });

    this._loadError.set(null);
    this._loading.set(true);
    this.inflight.set(key, promise);
    return promise;
  }

  getForAgency(agencyId: string): Promise<ConsolidatedPermissionsSnapshot> {
    const epoch = this.requestEpoch;
    const settled = this.agencySnapshots.get(agencyId);
    if (settled && settled.expiresAt > Date.now()) {
      return Promise.resolve(settled.snapshot);
    }
    // In-memory only would start cold on every reload, which is exactly when
    // the shell sweeps every agency at once. Serve the stored one and refresh
    // behind it.
    const stored = this.readCachedSnapshot([agencyId]);
    if (stored) {
      void this.refreshAgencySnapshot(agencyId, epoch);
      return Promise.resolve(stored);
    }
    const existing = this.agencyInflight.get(agencyId);
    if (existing) {
      return existing.then(snapshot =>
        epoch === this.requestEpoch
          ? snapshot
          : { ...NEUTRAL_PERMISSIONS_SNAPSHOT }
      );
    }

    const promise = this.fetchConsolidated(agencyId ? [agencyId] : [])
      .then(snapshot => {
        if (epoch !== this.requestEpoch) {
          return { ...NEUTRAL_PERMISSIONS_SNAPSHOT };
        }
        this.rememberAgencySnapshot(agencyId, snapshot);
        return snapshot;
      })
      .finally(() => {
        if (this.agencyInflight.get(agencyId) === promise) {
          this.agencyInflight.delete(agencyId);
        }
      });
    this.agencyInflight.set(agencyId, promise);
    return promise;
  }

  private rememberAgencySnapshot(
    agencyId: string,
    snapshot: ConsolidatedPermissionsSnapshot
  ): void {
    this.agencySnapshots.set(agencyId, {
      snapshot,
      expiresAt: Date.now() + AGENCY_SNAPSHOT_TTL_MS,
    });
    this.writeCachedSnapshot([agencyId], snapshot);
  }

  private async refreshAgencySnapshot(
    agencyId: string,
    epoch: number
  ): Promise<void> {
    if (this.agencyInflight.has(agencyId)) {
      return;
    }
    const promise = this.fetchConsolidated([agencyId])
      .then(snapshot => {
        if (epoch === this.requestEpoch) {
          this.rememberAgencySnapshot(agencyId, snapshot);
        }
        return snapshot;
      })
      .finally(() => {
        if (this.agencyInflight.get(agencyId) === promise) {
          this.agencyInflight.delete(agencyId);
        }
      });
    this.agencyInflight.set(agencyId, promise);
    try {
      await promise;
    } catch {
      // Keep the stored snapshot; the next navigation tries again.
    }
  }

  clear(): void {
    this.clearCachedSnapshots();
    clearGates();
    this.requestEpoch += 1;
    this.latestRequestKey = null;
    this.inflight.clear();
    this.agencyInflight.clear();
    this.agencySnapshots.clear();
    this._consolidated.set(null);
    this._selectedAgencyIds.set([]);
    this._loading.set(false);
    this._loadError.set(null);
    this.consolidatedSubject.next({ ...NEUTRAL_PERMISSIONS_SNAPSHOT });
  }

  /** Scoped to the signed-in user: another account must never read this back. */
  private snapshotCacheKey(agencyIds: string[]): string | undefined {
    // Firebase's uid, not the profile's: the profile arrives from
    // `/v1/user/me`, which on a cold load is still in flight when the first
    // consolidated request goes out — so keying on it skipped the cache
    // precisely when it was needed.
    const uid = this.auth.signedInUid();
    return uid
      ? `${SNAPSHOT_CACHE_PREFIX}${uid}.${agencyIds.join('|')}`
      : undefined;
  }

  private readCachedSnapshot(
    agencyIds: string[]
  ): ConsolidatedPermissionsSnapshot | undefined {
    const key = this.snapshotCacheKey(agencyIds);
    if (!key) {
      return undefined;
    }
    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        return undefined;
      }
      const cached = JSON.parse(raw) as CachedSnapshot;
      // `p` is the contract every consumer reads; a record without it would be
      // published as a snapshot and quietly skip the grant pre-checks. A
      // missing or future `savedAt` would also never expire.
      const age = Date.now() - cached?.savedAt;
      if (
        !cached?.snapshot?.p ||
        !Number.isFinite(age) ||
        age < 0 ||
        age > SNAPSHOT_CACHE_TTL_MS
      ) {
        localStorage.removeItem(key);
        return undefined;
      }
      return cached.snapshot;
    } catch {
      return undefined;
    }
  }

  private writeCachedSnapshot(
    agencyIds: string[],
    snapshot: ConsolidatedPermissionsSnapshot
  ): void {
    const key = this.snapshotCacheKey(agencyIds);
    if (!key) {
      return;
    }
    try {
      localStorage.setItem(
        key,
        JSON.stringify({
          savedAt: Date.now(),
          snapshot,
        } satisfies CachedSnapshot)
      );
    } catch {
      // Private mode or a full quota: the fetch still answers.
    }
  }

  /** Sign-out must not leave one user's permissions on a shared machine. */
  private clearCachedSnapshots(): void {
    try {
      const stale = Object.keys(localStorage).filter(key =>
        key.startsWith(SNAPSHOT_CACHE_PREFIX)
      );
      for (const key of stale) {
        localStorage.removeItem(key);
      }
    } catch {
      // Nothing to clear when storage is unavailable.
    }
  }

  private async fetchConsolidated(
    agencyIds: string[]
  ): Promise<ConsolidatedPermissionsSnapshot> {
    const response = await this.api.get<ConsolidatedPermissionsResponse>(
      this.buildConsolidatedPermissionsPath(agencyIds)
    );
    return {
      p: response.p as ProcessAwarePermissions,
      sad: response.sad,
      userType: userTypeFromJSON(response.userType),
      hasTags: response.hasTags,
      allTags: response.allTags ?? [],
      policyTagsLimited: response.policyTagsLimited === true,
      agencyId: response.agencyId,
      sourceAgencyIds: response.sourceAgencyIds ?? [],
      selectionAgencyIds: agencyIds,
    };
  }

  private buildConsolidatedPermissionsPath(agencyIds: string[]): string {
    if (agencyIds.length === 0) {
      return 'user/me/permissions/consolidated';
    }

    const params = new URLSearchParams();
    for (const agencyId of agencyIds) {
      params.append('agencyIds', agencyId);
    }
    return `user/me/permissions/consolidated?${params.toString()}`;
  }

  private normalizeSelectedAgencyIds(
    selection: BaseUser[] | null | undefined
  ): string[] {
    const currentUserId = this.auth.currentUser()?.id;
    const selectedUsers = selection ?? [];
    if (
      selectedUsers.length === 1 &&
      (selectedUsers[0].id === currentUserId ||
        !this.isAgencySelection(selectedUsers[0]))
    ) {
      return [];
    }

    const selectedIds = selectedUsers
      .map(user => user.id)
      .filter((id): id is string => !!id);

    return [...new Set(selectedIds)].sort();
  }

  private isCurrentRequest(epoch: number, key: string): boolean {
    return epoch === this.requestEpoch && this.latestRequestKey === key;
  }

  private isAgencySelection(user: BaseUser): boolean {
    return (
      user.userType === UserType.AGENCY || user.userType === UserType.PUBLISHER
    );
  }
}
